import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Swal from "sweetalert2";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBTable,
} from "mdbreact";
import { axioKit, fullName } from "../../../../../services/utilities";
import {
  formatDate,
  getCalibrationStatus,
  getNextPmDate,
} from "../../../serviceEngineer/machines/shared/utils";

const MAINTENANCE_URL = "procurements/operationals/machines/maintenances";
const MACHINES_URL = "procurements/operationals/machines/fixedAssets";

const TYPES = {
  preventive: { label: "Preventive maintenance", color: "info", icon: "tools" },
  calibration: { label: "Calibration", color: "primary", icon: "sliders-h" },
  repair: { label: "Repair", color: "danger", icon: "wrench" },
  inspection: { label: "Inspection", color: "secondary", icon: "search" },
};

const CONDITIONS = [
  { value: "fully functional", label: "Fully functional", color: "success" },
  { value: "functional", label: "Functional", color: "info" },
  { value: "damaged", label: "Damaged", color: "warning" },
  { value: "broken", label: "Broken", color: "danger" },
];
const conditionOf = (value) => CONDITIONS.find((c) => c.value === value);

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  machineId: "",
  type: "preventive",
  performedAt: today(),
  engineer: "",
  purpose: "",
  recommendations: "",
  condition: "",
});

const machineName = (machine = {}) =>
  [machine.brand, machine.model].filter(Boolean).join(" ") || "Unnamed machine";

/**
 * Merchandise > Maintenance: log of the services done on the machines of the
 * branch (PM, calibration, repair, inspection). A PM or calibration becomes
 * the machine's last PM, and the condition after it the machine's status.
 */
export default function Maintenance() {
  const { token, activePlatform, auth } = useSelector(({ auth }) => auth),
    branchId = activePlatform?.branchId,
    [records, setRecords] = useState([]),
    [machines, setMachines] = useState([]),
    [isLoading, setIsLoading] = useState(false),
    [isSaving, setIsSaving] = useState(false),
    [search, setSearch] = useState(""),
    [type, setType] = useState(""),
    [machineFilter, setMachineFilter] = useState(""),
    [form, setForm] = useState(null), // { ...fields, _id? } when the form is open
    { addToast } = useToasts();

  const notify = useCallback(
    (message, appearance = "error") =>
      addToast(message, { appearance, autoDismiss: true }),
    [addToast],
  );

  const load = useCallback(() => {
    if (!token || !branchId) return;
    setIsLoading(true);
    Promise.all([
      axioKit.universal(`${MAINTENANCE_URL}/browse`, token, { branchId }),
      axioKit.universal(`${MACHINES_URL}/browse`, token, { branchId }),
    ])
      .then(([_records, _machines]) => {
        setRecords(_records?.payload || []);
        setMachines(
          (_machines?.payload || []).filter(
            ({ branch }) => String(branch?._id || branch) === String(branchId),
          ),
        );
      })
      .catch((error) => notify(error.message))
      .finally(() => setIsLoading(false));
  }, [token, branchId, notify]);

  useEffect(() => {
    load();
  }, [load]);

  // machines whose PM is overdue or due soon, to be serviced next
  const due = useMemo(
    () =>
      machines
        .map((machine) => ({ machine, pm: getCalibrationStatus(machine) }))
        .filter(({ pm }) => ["danger", "warning"].includes(pm.tone))
        .sort((a, b) => (a.pm.daysLeft ?? 0) - (b.pm.daysLeft ?? 0)),
    [machines],
  );

  const visible = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return records.filter((record) => {
      if (type && record.type !== type) return false;
      if (machineFilter && String(record.machineId?._id) !== machineFilter)
        return false;
      const text = [
        machineName(record.machineId),
        record.machineId?.serial,
        record.engineer,
        record.purpose,
        record.recommendations,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return words.every((word) => text.includes(word));
    });
  }, [records, search, type, machineFilter]);

  const openNew = (machineId = "") => setForm({ ...emptyForm(), machineId });
  const openEdit = (record) =>
    setForm({
      _id: record._id,
      machineId: record.machineId?._id || "",
      type: record.type || "preventive",
      performedAt: (record.performedAt || record.createdAt || "").slice(0, 10),
      engineer: record.engineer || "",
      purpose: record.purpose || "",
      recommendations: record.recommendations || "",
      condition: record.condition || "",
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.machineId) return notify("Select the machine.", "warning");
    setIsSaving(true);
    try {
      const { _id, ...fields } = form;
      const data = {
        ...fields,
        performedAt: fields.performedAt || today(),
        condition: fields.condition || undefined,
        branchId,
      };
      const result = _id
        ? await axioKit.update(MAINTENANCE_URL, { ...data, _id }, token)
        : await axioKit.save(MAINTENANCE_URL, { ...data, userId: auth?._id }, token);
      notify(result?.success || "Saved.", "success");
      setForm(null);
      load();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (record) =>
    Swal.fire({
      title: "Delete this record?",
      text: `${TYPES[record.type]?.label || "Maintenance"} of ${machineName(
        record.machineId,
      )}. The machine itself is not changed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    }).then(async ({ isConfirmed }) => {
      if (!isConfirmed) return;
      try {
        await axioKit.destroy(MAINTENANCE_URL, { _id: record._id }, token);
        notify("Record deleted.", "success");
        load();
      } catch (error) {
        notify(error.message);
      }
    });

  const set = (key) => ({ target }) =>
    setForm((prev) => ({ ...prev, [key]: target.value }));
  const updatesPm = form && ["preventive", "calibration"].includes(form.type);

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="tools" className="mr-2 text-primary" />
              Maintenance
            </h5>
            <small className="text-muted">
              Services done on the machines: preventive maintenance,
              calibration, repair and inspection.
            </small>
          </div>
          <MDBBtn
            size="sm"
            color="primary"
            className="m-0"
            onClick={() => openNew()}
            disabled={!machines.length}
            title={machines.length ? "" : "No machine registered for this branch"}
          >
            <MDBIcon icon="plus" className="mr-1" /> Log maintenance
          </MDBBtn>
        </div>

        {/* due */}
        {due.length > 0 && (
          <div className="border rounded p-2 mb-3" style={{ background: "#fff8e1" }}>
            <div className="small font-weight-bold mb-1">
              <MDBIcon icon="calendar-times" className="mr-1 text-warning" />
              PM due
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 6 }}>
              {due.map(({ machine, pm }) => (
                <MDBBtn
                  key={machine._id}
                  size="sm"
                  color={pm.tone === "danger" ? "danger" : "warning"}
                  className="m-0 px-2 z-depth-0"
                  style={{ textTransform: "none" }}
                  title="Log its maintenance"
                  onClick={() => openNew(machine._id)}
                >
                  {machineName(machine)} · {pm.label.toLowerCase()} (
                  {formatDate(getNextPmDate(machine))})
                </MDBBtn>
              ))}
            </div>
          </div>
        )}

        {/* filters */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="btn-group flex-wrap" role="group" aria-label="Type">
            {[["", "All"], ...Object.entries(TYPES).map(([k, v]) => [k, v.label])].map(
              ([key, text]) => (
                <button
                  key={key || "all"}
                  type="button"
                  className={`btn btn-sm m-0 z-depth-0 ${
                    type === key ? "btn-primary" : "btn-outline-primary"
                  }`}
                  style={{ textTransform: "none" }}
                  onClick={() => setType(key)}
                >
                  {text}
                </button>
              ),
            )}
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <select
              className="browser-default custom-select custom-select-sm"
              style={{ width: 200 }}
              value={machineFilter}
              onChange={({ target }) => setMachineFilter(target.value)}
              aria-label="Machine"
            >
              <option value="">All machines</option>
              {machines.map((machine) => (
                <option key={machine._id} value={machine._id}>
                  {machineName(machine)}
                </option>
              ))}
            </select>
            <div className="position-relative" style={{ width: 240 }}>
              <MDBIcon
                icon="search"
                className="position-absolute text-muted"
                style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
              />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30 }}
                placeholder="Search machine, engineer, findings"
                value={search}
                onChange={({ target }) => setSearch(target.value)}
              />
            </div>
          </div>
        </div>

        <MDBTable responsive hover small className="mb-0">
          <thead className="grey lighten-4">
            <tr>
              <th>Date</th>
              <th>Machine</th>
              <th>Service</th>
              <th>Findings / recommendation</th>
              <th>Condition after</th>
              <th className="text-center" style={{ width: 90 }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {!visible.length && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-5">
                  {isLoading
                    ? "Loading records…"
                    : !records.length
                      ? "No maintenance logged yet."
                      : "No record matches your filters."}
                </td>
              </tr>
            )}
            {visible.map((record) => {
              const service = TYPES[record.type] || TYPES.preventive;
              const condition = conditionOf(record.condition);
              return (
                <tr key={record._id}>
                  <td className="align-middle text-nowrap">
                    {formatDate(record.performedAt || record.createdAt)}
                  </td>
                  <td className="align-middle">
                    <span className="font-weight-bold">
                      {machineName(record.machineId)}
                    </span>
                    <small className="d-block text-muted">
                      {[record.machineId?.serial && `S/N ${record.machineId.serial}`, record.machineId?.section]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  </td>
                  <td className="align-middle">
                    <MDBBadge color={service.color}>
                      <MDBIcon icon={service.icon} className="mr-1" />
                      {service.label}
                    </MDBBadge>
                    <small className="d-block text-muted">
                      {record.engineer ? `by ${record.engineer}` : "Engineer not set"}
                      {record.userId?.fullName &&
                        ` · logged by ${fullName(record.userId.fullName)}`}
                    </small>
                  </td>
                  <td className="align-middle small" style={{ maxWidth: 320 }}>
                    {record.purpose && <div>{record.purpose}</div>}
                    {record.recommendations && (
                      <div className="text-muted">{record.recommendations}</div>
                    )}
                  </td>
                  <td className="align-middle">
                    {condition ? (
                      <MDBBadge color={condition.color}>{condition.label}</MDBBadge>
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                  <td className="align-middle text-center py-1 text-nowrap">
                    <MDBBtn
                      size="sm"
                      color="primary"
                      className="m-0 px-2"
                      title="Edit"
                      onClick={() => openEdit(record)}
                    >
                      <MDBIcon icon="pencil-alt" />
                    </MDBBtn>
                    <MDBBtn
                      size="sm"
                      color="danger"
                      className="m-0 ml-1 px-2"
                      title="Delete"
                      onClick={() => handleDelete(record)}
                    >
                      <MDBIcon icon="trash" />
                    </MDBBtn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </MDBTable>

        <MDBModal isOpen={!!form} toggle={() => !isSaving && setForm(null)} backdrop>
          <MDBModalHeader
            toggle={() => !isSaving && setForm(null)}
            className="light-blue darken-3 white-text"
          >
            <MDBIcon icon="tools" className="mr-2" />
            {form?._id ? "Edit maintenance" : "Log maintenance"}
          </MDBModalHeader>
          <MDBModalBody>
            {form && (
              <form onSubmit={handleSubmit}>
                <label className="small text-muted mb-1">Machine</label>
                <select
                  className="browser-default custom-select mb-3"
                  value={form.machineId}
                  onChange={set("machineId")}
                  required
                >
                  <option value="">Select the machine</option>
                  {machines.map((machine) => (
                    <option key={machine._id} value={machine._id}>
                      {machineName(machine)}
                      {machine.serial ? ` · S/N ${machine.serial}` : ""}
                    </option>
                  ))}
                </select>

                <div className="row">
                  <div className="col-md-7">
                    <label className="small text-muted mb-1">Service</label>
                    <select
                      className="browser-default custom-select mb-3"
                      value={form.type}
                      onChange={set("type")}
                    >
                      {Object.entries(TYPES).map(([value, { label }]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="small text-muted mb-1">Date done</label>
                    <input
                      type="date"
                      className="form-control mb-3"
                      value={form.performedAt}
                      max={today()}
                      onChange={set("performedAt")}
                      required
                    />
                  </div>
                </div>

                <label className="small text-muted mb-1">Engineer / technician</label>
                <input
                  className="form-control mb-3"
                  value={form.engineer}
                  onChange={set("engineer")}
                  placeholder="Name of the one who did the service"
                />

                <label className="small text-muted mb-1">What was done / found</label>
                <textarea
                  className="form-control mb-3"
                  rows={2}
                  value={form.purpose}
                  onChange={set("purpose")}
                  placeholder="e.g. cleaned the probes, replaced the tubing"
                />

                <label className="small text-muted mb-1">Recommendation / next steps</label>
                <textarea
                  className="form-control mb-3"
                  rows={2}
                  value={form.recommendations}
                  onChange={set("recommendations")}
                  placeholder="e.g. no issues found, replace the lamp next PM"
                />

                <label className="small text-muted mb-1">Condition after the service</label>
                <select
                  className="browser-default custom-select mb-2"
                  value={form.condition}
                  onChange={set("condition")}
                >
                  <option value="">Not changed</option>
                  {CONDITIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>

                <small className="d-block text-muted mb-3">
                  <MDBIcon icon="info-circle" className="mr-1" />
                  {updatesPm
                    ? "This date becomes the machine's last PM"
                    : "The machine's PM date is not changed"}
                  {form.condition ? ", and its condition is updated." : "."}
                </small>

                <div className="text-right">
                  <MDBBtn
                    type="button"
                    color="light"
                    className="m-0 mr-2"
                    onClick={() => setForm(null)}
                    disabled={isSaving}
                  >
                    Cancel
                  </MDBBtn>
                  <MDBBtn type="submit" color="primary" className="m-0" disabled={isSaving}>
                    {isSaving && <MDBIcon icon="spinner" spin className="mr-1" />}
                    Save
                  </MDBBtn>
                </div>
              </form>
            )}
          </MDBModalBody>
        </MDBModal>
      </MDBCardBody>
    </MDBCard>
  );
}
